import { useState } from 'react'
import {
  Button,
  ButtonGroup,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

type StreamlineAction = 'saveAndDispatch' | 'saveBooking' | 'saveAndClose'

const ACTION_LABELS: Record<StreamlineAction, string> = {
  saveAndDispatch: 'Save and Dispatch',
  saveBooking: 'Save Booking',
  saveAndClose: 'Save and Close',
}

const MENU_BG = '#0095E8'

const menuItemSx = {
  backgroundColor: MENU_BG,
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
  },
}

interface StreamlineSaveDropdownProps {
  disabled?: boolean
  loading?: boolean
  onSaveAndDispatch: () => void
  onSaveBooking: () => void
  onSaveAndClose: () => void
  className?: string
}

const StreamlineSaveDropdown = ({  disabled = false,
  loading = false,
  onSaveAndDispatch,
  onSaveBooking,
  onSaveAndClose,
  className = '',
}: StreamlineSaveDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedAction, setSelectedAction] = useState<StreamlineAction>('saveAndDispatch')
  const open = Boolean(anchorEl)

  const handleClose = () => setAnchorEl(null)

  const executeAction = (action: StreamlineAction) => {
    if (action === 'saveAndDispatch') onSaveAndDispatch()
    else if (action === 'saveBooking') onSaveBooking()
    else onSaveAndClose()
  }

  const handleSelect = (action: StreamlineAction) => {
    handleClose()
    setSelectedAction(action)
    executeAction(action)
  }

  const handleMainClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (!disabled && !loading) {
      executeAction(selectedAction)
    }
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (!disabled && !loading) {
      setAnchorEl(event.currentTarget)
    }
  }

  return (
    <>
      <ButtonGroup
        className={className}
        sx={{ boxShadow: 'none' }}
      >
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center justify-content-center"
          disabled={disabled || loading}
          style={{'border-top-right-radius': 0, 'border-bottom-right-radius': 0}}
          onClick={handleMainClick}
          // sx={{
          //   textTransform: 'none',
          //   boxShadow: 'none',
          //   borderTopRightRadius: 0,
          //   borderBottomRightRadius: 0,
          // }}
        >
          {ACTION_LABELS[selectedAction]}
          {loading && (
            <CircularProgress size={16} sx={{ color: 'white', ml: 1 }} />
          )}
        </button>
        <Button
          type="button"
          className="btn btn-primary d-flex align-items-center justify-content-center px-2"
          disabled={disabled || loading}
          onClick={handleOpenMenu}
          sx={{
            boxShadow: 'none',
            borderLeft: '1px solid rgba(255,255,255,0.4)',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            minWidth: 36,
          }}
        >
          <KeyboardArrowDownIcon />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: MENU_BG,
            borderRadius: '6px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: 220,
            overflow: 'hidden',
          },
        }}
        MenuListProps={{
          sx: {
            backgroundColor: MENU_BG,
            py: 0.5,
          },
        }}
      >
        <MenuItem sx={menuItemSx} selected={selectedAction === 'saveAndDispatch'} onClick={() => handleSelect('saveAndDispatch')}>
          <ListItemText
            primary={ACTION_LABELS.saveAndDispatch}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}
          />
        </MenuItem>
        <MenuItem sx={menuItemSx} selected={selectedAction === 'saveBooking'} onClick={() => handleSelect('saveBooking')}>
          <ListItemText
            primary={ACTION_LABELS.saveBooking}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}
          />
        </MenuItem>
        <MenuItem sx={menuItemSx} selected={selectedAction === 'saveAndClose'} onClick={() => handleSelect('saveAndClose')}>
          <ListItemText
            primary={ACTION_LABELS.saveAndClose}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

export default StreamlineSaveDropdown
